import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, ReactiveFormsModule, Validators } from '@angular/forms';

import { ButtonModule, SimpleInputModule } from '@hglobal/common';
import { FormBuilder, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { GroupResolverFormBuilder } from '@ngneat/reactive-forms/lib/form-builder';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { tap } from 'rxjs';

import { TooltipComponent } from '@pp/common';
import { Account, parseRoleFromApi, Team } from '@pp/core/entities';
import { FormValidationService } from '@pp/core/services';
import { DictionariesService } from '@pp/dictionaries';

import { EditUserProfileReq } from '../personal-info.entities';

@Component({
  selector: 'pp-user-profile-form',
  imports: [CommonModule, ButtonModule, ReactiveFormsModule, SimpleInputModule, TranslateModule, TooltipComponent],
  templateUrl: './form.component.html',
  styleUrl: './form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileFormComponent implements OnInit {
  readonly #formValidationService = inject(FormValidationService);
  readonly #fb = inject(FormBuilder);
  readonly #translateService = inject(TranslateService);
  readonly #dictionariesService = inject(DictionariesService);
  readonly #destroyRef = inject(DestroyRef);
  readonly user = input<Account>();
  readonly org = input<Team>();
  readonly readonlyRights = input<boolean>();
  readonly close = output<void>();
  readonly submitForm = output<EditUserProfileReq>();
  readonly unsavedChanges = output<boolean>();

  form: FormGroup<GroupResolverFormBuilder<EditUserProfileReq>>;
  hasChanges = signal<boolean>(false);

  unsavedChangesEffect = effect(() => {
    this.unsavedChanges.emit(this.hasChanges());
  });

  get phoneCtrl(): AbstractControl {
    return this.form.get('phone');
  }

  get emailCtrl(): AbstractControl {
    return this.form.get('email');
  }

  get isEmailChanged(): boolean {
    return this.emailCtrl.value.trim() !== this.user().email.trim();
  }

  get isPhoneChanged(): boolean {
    return this.phoneCtrl.value.trim() !== this.#getPhoneNumber(this.user().phone);
  }

  get #roleKey(): string {
    return this.#dictionariesService.roles.get(parseRoleFromApi(this.user().role));
  }

  ngOnInit() {
    this.#initForm();
    this.#subscribeOnFormChanges();

    if (this.readonlyRights()) return;

    this.#subscribeOnPhone();
    this.#subscribeOnEmail();
  }

  discardChanges(): void {
    const phone = this.#getPhoneNumber(this.user().phone);
    const email = this.user().email;
    this.form.patchValue({ phone, email });
  }

  save(): void {
    const req: EditUserProfileReq = {};
    if (this.phoneCtrl.enabled) {
      req.phone = this.#formValidationService.cleanPhoneNumber(this.phoneCtrl.value).trim();
    }
    if (this.emailCtrl.enabled) {
      req.email = this.emailCtrl.value.trim();
    }
    this.submitForm.emit(req);
  }

  #initForm(): void {
    this.form = this.#fb.group({
      fullName: new FormControl({ value: this.#getFullName(), disabled: true }, Validators.required),
      phone: new FormControl({ value: this.#getPhoneNumber(this.user().phone), disabled: this.readonlyRights() }, [
        Validators.required,
        this.#formValidationService.phoneValidator(),
      ]),
      email: new FormControl({ value: this.user().email, disabled: this.readonlyRights() }, [
        Validators.required,
        this.#formValidationService.emailValidator(),
      ]),
      roleName: new FormControl({ value: this.#getRole(), disabled: true }, Validators.required),
      orgName: new FormControl({ value: this.org()?.name, disabled: true }, Validators.required),
      currency: new FormControl({ value: 'EUR', disabled: true }, Validators.required),
    });
  }

  #subscribeOnPhone(): void {
    this.phoneCtrl.valueChanges
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        tap(() => this.#handleDisablingEmail()),
      )
      .subscribe();
  }

  #subscribeOnEmail(): void {
    this.emailCtrl.valueChanges
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        tap(() => this.#handleDisablingPhone()),
      )
      .subscribe();
  }

  #subscribeOnFormChanges(): void {
    this.form.valueChanges
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        tap(() => this.#checkOnChanges()),
      )
      .subscribe();
  }

  #handleDisablingEmail(): void {
    this.isPhoneChanged ? this.emailCtrl.disable({ emitEvent: false }) : this.emailCtrl.enable({ emitEvent: false });
  }

  #handleDisablingPhone(): void {
    this.isEmailChanged ? this.phoneCtrl.disable({ emitEvent: false }) : this.phoneCtrl.enable({ emitEvent: false });
  }

  #checkOnChanges(): void {
    this.hasChanges.set(this.isPhoneChanged || this.isEmailChanged);
  }

  #getPhoneNumber(phone: string): string {
    return `+${phone}`.trim();
  }

  #getRole(): string {
    return this.#translateService.instant(this.#roleKey);
  }

  #getFullName(): string {
    return `${this.user().name} ${this.user().surname}`;
  }
}
